/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import liveSearchFacetsBh from './bh.js'
import liveSearchFacetsMapping from './mapping.js'
import liveSearchFacetsOptions from './options.js'
import liveSearchFacetsReset from './reset.js'
import liveSearchFacetsState from './state.js'
import { hasFacetBucketsForAttributes } from './utils/bh-facet-visibility.js'

const FILTERS_EXPANDED_STORAGE_KEY = 'liveSearchFiltersExpandedState'

const liveSearchFacets = {
    ...liveSearchFacetsBh,
    ...liveSearchFacetsMapping,
    ...liveSearchFacetsOptions,
    ...liveSearchFacetsReset,
    ...liveSearchFacetsState,
    handleSelectedFacetsChange() {
        if (this.hasSeoIndexedFilters) {
            return
        }

        // Group selected facets by attribute
        const facetsByAttribute = this.selectedFacets.reduce((map, { attribute, option }) => {
            map.set(attribute, [...(map.get(attribute) || []), option])
            return map
        }, new Map())

        // Update URL parameters for each attribute
        facetsByAttribute.forEach((options, attribute) => {
            this.addOrUpdateUrlParam(attribute, options.join(','))
        })

        // Remove URL parameters that are not facet keys we care about
        const keysToRemove = []
        this.urlParams.forEach((_, key) => {
            const decodedKey = this.safeDecode(key)
            const isAllowed = this.isFacetAttributeAllowed(decodedKey)

            if (!facetsByAttribute.has(decodedKey) && isAllowed) {
                keysToRemove.push(key)
            }
        })

        keysToRemove.forEach(key => this.removeUrlParam(key, false))
        this.updateUrl()
    },
    /**
     * Update facets based on the fetched data
     *
     * @param {Array} facets - The array of fetched facets.
     */
    updateFacets(facets) {
        if (!facets.length) {
            this.facets = []
            return
        }

        const processedFacets = this.processArray(
            facets,
            facet => this.facetConfig.bhAttributes.includes(facet.attribute),
            this.facetConfig.mergedBhLabel,
            this.facetConfig.mergedBhAttribute
        )

        const shouldShowBhFacet = hasFacetBucketsForAttributes(facets, this.facetConfig.bhAttributes)

        this.facets = this.handleFacets(processedFacets)
            .filter(facet => facet.options)
            // Prevent rendering stale BH filter when both bh_size and cup_size have no buckets
            .filter(facet => shouldShowBhFacet || facet.attribute !== this.facetConfig.mergedBhAttribute)

        this.syncFiltersExpandedState()
    },
    /**
     * Get visible single button facets.
     *
     * @returns {Array}
     */
    getVisibleSingleButtonFacets() {
        return this.facets.filter(
            facet => facet.isSingleButton && this.setVisibleState(facet)
        )
    },
    /**
     * Get visible facets used for helper button limit.
     *
     * @returns {Array}
     */
    getVisibleFacetsForHelper() {
        return this.facets.filter(facet => this.setVisibleState(facet))
    },
    /**
     * Get maximum number of visible facets before helper button appears.
     *
     * @returns {number}
     */
    getFiltersVisibleLimit() {
        const configuredLimit = Number(this.facetConfig?.filtersVisibleLimit)

        return Number.isInteger(configuredLimit) && configuredLimit > 0
            ? configuredLimit
            : 8
    },
    /**
     * Get visible non-single facets rendered as dropdowns.
     *
     * @returns {Array}
     */
    getVisibleDropdownFacets() {
        return this.getVisibleFacetsForHelper().filter(
            facet => !facet.isSingleButton && this.isFacetWithinVisibleLimit(facet.attribute)
        )
    },
    /**
     * Check if the current facet is the last visible dropdown facet.
     *
     * @param {string} attribute
     * @returns {boolean}
     */
    isLastVisibleDropdownFacet(attribute) {
        const visibleDropdownFacets = this.getVisibleDropdownFacets()
        if (visibleDropdownFacets.length < 1) {
            return false
        }

        return visibleDropdownFacets[visibleDropdownFacets.length - 1].attribute === attribute
    },
    /**
     * Handle mobile facet toggle and scroll last dropdown into view.
     *
     * @param {string} attribute
     * @param {boolean} expanded
     */
    handleMobileLastFacetExpand(attribute, expanded) {
        if (!expanded || !this.isMobile || !this.isLastVisibleDropdownFacet(attribute)) {
            return
        }

        this.scrollFacetOptionsIntoMobileView(attribute)
    },
    /**
     * Scroll expanded mobile facet options into offcanvas viewport.
     *
     * @param {string} attribute
     */
    scrollFacetOptionsIntoMobileView(attribute) {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return
        }

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const scrollContainer = document.getElementById('facets-offcanvas-scroll')
                const optionsContainer = document.getElementById(attribute)

                if (!scrollContainer || !optionsContainer) {
                    return
                }

                const containerRect = scrollContainer.getBoundingClientRect()
                const optionsRect = optionsContainer.getBoundingClientRect()
                const targetTop = scrollContainer.scrollTop + (optionsRect.top - containerRect.top) - 12
                const nextScrollTop = Math.max(targetTop, 0)

                if (typeof scrollContainer.scrollTo === 'function') {
                    scrollContainer.scrollTo({ top: nextScrollTop, behavior: 'smooth' })
                    return
                }

                scrollContainer.scrollTop = nextScrollTop
            })
        })
    },
    /**
     * Expand hidden facets.
     */
    expandFilters() {
        this.filtersExpanded = true
        this.persistFiltersExpandedState(true)
    },
    /**
     * Persist helper-expanded state for current page in session storage.
     *
     * @param {boolean} expanded
     */
    persistFiltersExpandedState(expanded) {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return
        }

        if (!expanded) {
            window.sessionStorage.removeItem(FILTERS_EXPANDED_STORAGE_KEY)
            return
        }

        window.sessionStorage.setItem(FILTERS_EXPANDED_STORAGE_KEY, '1')
    },
    /**
     * Resolve persisted helper-expanded state for current page.
     *
     * @returns {boolean}
     */
    getPersistedFiltersExpandedState() {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return false
        }

        return window.sessionStorage.getItem(FILTERS_EXPANDED_STORAGE_KEY) === '1'
    },
    /**
     * Check whether a facet should be visible in collapsed helper state.
     *
     * @param {string} attribute
     * @returns {boolean}
     */
    isFacetWithinVisibleLimit(attribute) {
        if (this.filtersExpanded) {
            return true
        }

        const limit = this.getFiltersVisibleLimit()
        const index = this.getVisibleFacetsForHelper().findIndex(facet => facet.attribute === attribute)

        return index > -1 && index < limit
    },
    /**
     * Check if hidden facet is selected.
     *
     * @returns {boolean}
     */
    hasSelectedFilterBeyondLimit() {
        const limit = this.getFiltersVisibleLimit()

        return this.getVisibleFacetsForHelper().some(
            (facet, index) => index >= limit && this.selectedFacets.some(
                selected => selected.attribute === facet.attribute
            )
        )
    },
    /**
     * Ensure selected hidden facets are visible after reload.
     */
    syncFiltersExpandedState() {
        if (this.hasSelectedFilterBeyondLimit()) {
            this.filtersExpanded = true
            this.persistFiltersExpandedState(true)
            return
        }

        if (this.currentCategoryPath !== this.lastVisitedCategoryPath) {
            this.filtersExpanded = false
            this.persistFiltersExpandedState(false)
            return
        }

        this.filtersExpanded = this.getPersistedFiltersExpandedState()
    },
    /**
     * Check if helper button should be shown.
     *
     * @returns {boolean}
     */
    shouldShowFiltersHelper() {
        const shouldRestoreExpandedState = this.currentCategoryPath === this.lastVisitedCategoryPath &&
            this.getPersistedFiltersExpandedState()

        return !this.filtersExpanded && !shouldRestoreExpandedState &&
            this.getVisibleFacetsForHelper().length > this.getFiltersVisibleLimit()
    },
    /**
     * Sort numeric facet buckets by title
     *
     * @param {Array} facets - The array of facets to process.
     * @param {string} attribute - The attribute of the facet to sort.
     */
    sortNumericFacetBuckets(facets, attribute) {
        const facet = facets.find(f => f.attribute === attribute)
        if (facet && Array.isArray(facet.buckets)) {
            facet.buckets = facet.buckets.slice().sort((a, b) => Number(a.title) - Number(b.title))
        }
    },
    /**
     * Sort buckets of a facet by a custom order defined in an array
     *
     * @param facets
     * @param attribute
     * @param orderList
     */
    sortBucketsByCustomOrder(facets, attribute, orderList) {
        const facet = facets.find(f => f.attribute === attribute)
        if (!facet || !Array.isArray(facet.buckets)) {
            return
        }

        facet.buckets = facet.buckets.slice().sort((a, b) => {
            const indexA = orderList.indexOf(a.title.trim())
            const indexB = orderList.indexOf(b.title.trim())
            return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
        })
    },
    /**
     * Disable facets that are not available
     *
     * @param {Array} facets - The array of facets to process.
     * @returns {Array} - The updated array of facets with disabled options.
     */
    handleFacets(facets) {
        // Check if facets are already mapped and update options with disabled state
        if (this.facets.length) {
            return this.facets.map(facet => this.updateFacetOptions(facet, facets))
        }
        // If facets are not already mapped, map them
        return this.mapFacets(facets)
    },
    /**
     * Find the current facet from the facets array
     *
     * @param {Object} facet - The facet to find.
     * @param {Array} facets - The array of facets to search.
     * @returns {Object} - The found current facet.
     */
    findCurrentFacet(facet, facets) {
        return facets.find(f => f.attribute === facet.attribute)
    },
    /**
     * Handle facet click
     * used in facets-selected.phtml && facets-items.phtml
     *
     * @param $event
     * @param {string} attribute
     * @param {string} option
     */
    handleFacetClick($event, attribute, option) {
        const facetIndex = this.selectedFacets.findIndex(facet => facet.attribute === attribute && facet.option === option)

        if (facetIndex !== -1) {
            // Remove facet
            this.selectedFacets.splice(facetIndex, 1)
            this.removeFacetFocus($event)
        } else {
            // Add facet
            this.selectedFacets.push({ attribute, option })
        }

        if (this.hasSeoIndexedFilters) {
            // Reload the page if SEO indexed filters are used
            this.seoIndexedFilterPageReload()
            return
        }

        this.setUrlPageParam(1)
        this.resetProducts()
        this.getProducts('facets')

        if (!this.isMobile && this.facetConfig.singleButtonAttributes.includes(attribute)) {
            this.$dispatch('facets-expanded-close-all')
        }
        this.$dispatch('facets-changed', attribute)
    },
    /**
     * Remove focus from facet button
     * Workaround for deselected options highlighted as selected on mobile devices
     *
     * @param event
     */
    removeFacetFocus(event) {
        if (this.isMobile) {
            setTimeout(() => event.target.blur(), 300)
        }
    },
    /**
     * Get active facets count for the given attribute
     *
     * @param {string} attribute
     * @returns {number}
     */
    getActiveFacetsCount(attribute) {
        const selectedFacets = this.selectedFacets
        const isMergedBhAttribute = attribute === this.facetConfig.mergedBhAttribute
        return selectedFacets.filter(
            facet => isMergedBhAttribute
            ? this.facetConfig.bhAttributes.includes(facet.attribute)
            : facet.attribute === attribute || facet.attribute.includes(attribute)
        ).length
    },

    /**
     * Checks if a facet should be hidden from the list of facets.
     *
     * A facet should be hidden if it has only one option and that option is "no"
     *
     * @param {Object} facet - The facet to check.
     * @returns {boolean} - True if the facet should be hidden, false otherwise.
     */
    shouldHideFacet(facet) {
        // Hide if options are missing or empty
        if (!facet.options || facet.options.length === 0) {
            return true
        }
        // Hide if only one option and its id is "no"
        return facet.options.length === 1 && facet.options[0].id === 'no'
    },
    /**
     * Reload the page if SEO indexed filters are used
     * This is necessary to load the original category page url
     */
    seoIndexedFilterPageReload() {
        if (this.hasSeoIndexedFilters) {
            const targetBaseUrl = `${this.baseUrl}${this.currentCategoryPath}`
            const currentParams = new URLSearchParams(window.location.search)
            const queryParams = new URLSearchParams(currentParams.toString())

            queryParams.delete('filter_type')
            queryParams.delete('id')
            queryParams.delete('p')

            if (this.facetAttributeAllowlist) {
                this.facetAttributeAllowlist.forEach(attribute => {
                    queryParams.delete(attribute)
                })
            }

            const facetsByAttribute = this.selectedFacets.reduce((map, { attribute, option }) => {
                map.set(attribute, [...(map.get(attribute) || []), option])
                return map
            }, new Map())

            facetsByAttribute.forEach((options, attribute) => {
                queryParams.set(attribute, options.join(','))
            })

            const queryString = queryParams.toString()
            const targetUrl = queryString ? `${targetBaseUrl}?${queryString}` : targetBaseUrl

            window.location.replace(targetUrl)
        }
    }
}

export default liveSearchFacets
